import { useState, useEffect } from 'react';
import SuperAdminDashboard from '../components/receipts/SuperAdminDashboard';
import TeamLeaderDashboard from '../components/receipts/TeamLeaderDashboard';
import Dashboard from '../components/receipts/Dashboard';
import TaxReports from '../components/receipts/TaxReports';
import Settings from '../components/receipts/Settings';
import DevModePreview from '../components/receipts/DevModePreview';
import QRLogin from '../components/receipts/QRLogin';
import Login from '../components/receipts/Login';
import NotificationPanel from '../components/receipts/NotificationPanel';
import { MaintenanceWarningOverlay } from '../components/MaintenanceWarningOverlay';
import { MaintenanceOverlay } from '../components/MaintenanceOverlay';
import { Shield, Users, User, LayoutDashboard, FileText, Settings as SettingsIcon, Bell, Globe, Moon, Sun, LogOut, X, QrCode, Send, Zap } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { languages } from '../lib/translations';
import { supabase } from '../lib/supabase';
import { useAdminAuth } from '../contexts/AdminAuthContext';

type UserRole = 'super_admin' | 'team_leader' | 'normal_user';
type Page = 'dashboard' | 'tax-reports' | 'settings';

export default function Receipts() {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [isQRLogin, setIsQRLogin] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [viewAsUser, setViewAsUser] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [actualUserId, setActualUserId] = useState<string | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const { darkMode, toggleDarkMode, language, setLanguage } = useTheme();
  const { maintenanceStatus, isSuperAdmin: isSuperAdminAuth, loading: authLoading } = useAdminAuth();

  useEffect(() => {
    const savedUser = localStorage.getItem('lytspeed_receipts_user');
    const sessionExpiry = localStorage.getItem('lytspeed_receipts_session_expiry');

    if (savedUser && sessionExpiry) {
      try {
        const expiryDate = new Date(sessionExpiry);
        if (expiryDate > new Date()) {
          const session = JSON.parse(savedUser);
          setUserData(session);

          let role: UserRole;
          if (session.is_super_admin) {
            role = 'super_admin';
          } else if (session.role === 'team_leader') {
            role = 'team_leader';
          } else {
            role = 'normal_user';
          }

          setUserRole(role);
          setIsAuthenticated(true);

          if (session.telegram_id) {
            fetchActualUserId(session.telegram_id);
          }
        } else {
          localStorage.removeItem('lytspeed_receipts_user');
          localStorage.removeItem('lytspeed_receipts_session_token');
          localStorage.removeItem('lytspeed_receipts_session_expiry');
        }
      } catch (error) {
        localStorage.removeItem('lytspeed_receipts_user');
        localStorage.removeItem('lytspeed_receipts_session_token');
        localStorage.removeItem('lytspeed_receipts_session_expiry');
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setIsQRLogin(true);
    }
  }, []);

  async function fetchActualUserId(telegramId: number) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('telegram_id', telegramId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user ID:', error);
        return;
      }

      if (data) {
        console.log('Found actual user ID for telegram_id', telegramId, ':', data.id);
        setActualUserId(data.id);
      }
    } catch (error) {
      console.error('Error in fetchActualUserId:', error);
    }
  }

  const handleLoginSuccess = (user: any) => {
    console.log('[Login] User data received:', user);
    console.log('[Login] User role:', user.role);
    console.log('[Login] Is super admin:', user.is_super_admin);

    let determinedRole: UserRole;
    if (user.is_super_admin) {
      determinedRole = 'super_admin';
    } else if (user.role === 'team_leader') {
      determinedRole = 'team_leader';
    } else {
      determinedRole = 'normal_user';
    }
    console.log('[Login] Determined role:', determinedRole);

    setUserData(user);
    setUserRole(determinedRole);
    setIsAuthenticated(true);
    setIsQRLogin(false);
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24);
    localStorage.setItem('lytspeed_receipts_user', JSON.stringify(user));
    localStorage.setItem('lytspeed_receipts_session_expiry', expiresAt.toISOString());
    window.history.replaceState({}, '', '/');

    if (user.telegram_id) {
      fetchActualUserId(user.telegram_id);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserData(null);
    setUserRole(null);
    localStorage.removeItem('lytspeed_receipts_user');
    localStorage.removeItem('lytspeed_receipts_session_token');
    localStorage.removeItem('lytspeed_receipts_session_expiry');
    window.location.href = '/';
  };

  const handleRequestQRCode = () => {
    setShowQRModal(true);
  };

  const isDevelopmentMode = import.meta.env.VITE_DEVELOPMENT_MODE === 'true';
  const isSuperAdmin = userRole === 'super_admin';
  const effectiveRole = (isSuperAdmin && viewAsUser) ? 'normal_user' : (userRole || 'normal_user');

  console.log('[Receipts] Current state:', {
    isAuthenticated,
    userRole,
    effectiveRole,
    isDevelopmentMode,
    isQRLogin,
    userData: userData ? 'present' : 'null'
  });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (isQRLogin) {
    return <QRLogin onLoginSuccess={handleLoginSuccess} />;
  }

  if (!isAuthenticated && !isDevelopmentMode) {
    return (
      <>
        {maintenanceStatus?.isMaintenanceMode && !isSuperAdminAuth && maintenanceStatus.maintenanceEndsAt && (
          <MaintenanceOverlay
            maintenanceEndsAt={maintenanceStatus.maintenanceEndsAt}
            maintenanceMessage={maintenanceStatus.maintenanceMessage}
          />
        )}
        {maintenanceStatus?.isWarningMode && !maintenanceStatus.isMaintenanceMode && !isSuperAdminAuth && maintenanceStatus.warningStartedAt && (
          <MaintenanceWarningOverlay warningStartedAt={maintenanceStatus.warningStartedAt} />
        )}
        <Login onRequestQRCode={handleRequestQRCode} />
        {showQRModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowQRModal(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-gradient-to-r from-emerald-500 to-blue-600 p-6 relative">
                <button
                  onClick={() => setShowQRModal(false)}
                  className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Send className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Telegram Login</h2>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-300 mb-6">Follow these steps to login using Telegram:</p>

                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="text-gray-800 dark:text-gray-200 font-medium">Open your Telegram app</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Make sure you have Telegram installed</p>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="text-gray-800 dark:text-gray-200 font-medium">Search for <span className="font-mono text-blue-600 dark:text-blue-400">@LBRH1_bot</span></p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Find our official bot in the search</p>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="text-gray-800 dark:text-gray-200 font-medium">Send <span className="font-mono text-blue-600 dark:text-blue-400">/login</span> command</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Type and send the login command</p>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      4
                    </div>
                    <div>
                      <p className="text-gray-800 dark:text-gray-200 font-medium">Click the login link sent to you</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">The bot will send you a secure login link</p>
                    </div>
                  </li>
                </ol>

                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <Send className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Alternative Method</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">Use the email login option below to receive a magic link</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowQRModal(false)}
                  className="mt-6 w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white rounded-xl font-semibold transition-all transform hover:scale-105 shadow-lg"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {isDevelopmentMode && (
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-2 px-4 border-b-2 border-orange-300">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            <p className="text-sm font-bold uppercase tracking-wider">Development Mode Active</p>
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          </div>
        </div>
      )}

      <DevModePreview
        currentRole={userRole}
        onRoleChange={(role) => setUserRole(role)}
        currentUserId={selectedUserId}
        onUserChange={(userId) => setSelectedUserId(userId)}
      />

      {effectiveRole === 'super_admin' && (
        <div className="relative">
          {isSuperAdmin && (
            <div className="fixed top-20 right-4 z-50">
              <button
                onClick={() => setViewAsUser(!viewAsUser)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-lg transition-all"
              >
                {viewAsUser ? <Shield className="w-4 h-4" /> : <User className="w-4 h-4" />}
                <span className="text-sm font-medium">
                  {viewAsUser ? 'Switch to Admin View' : 'Switch to User View'}
                </span>
              </button>
            </div>
          )}
          <SuperAdminDashboard />
        </div>
      )}

      {effectiveRole === 'team_leader' && <TeamLeaderDashboard currentUserId={selectedUserId} />}

      {effectiveRole === 'normal_user' && (
        <div className="flex flex-col min-h-screen relative">
          {isSuperAdmin && (
            <div className="fixed top-20 right-4 z-50">
              <button
                onClick={() => setViewAsUser(!viewAsUser)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-lg transition-all"
              >
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">Switch to Admin View</span>
              </button>
            </div>
          )}
          <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
              <div className="flex justify-between items-center h-16 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-shrink">
                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg p-2 flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0 hidden sm:block">
                    <h1 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white truncate">LytSpeed Receipts</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Receipt Management</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 overflow-x-auto">
                  <button
                    onClick={() => setCurrentPage('dashboard')}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg transition-all flex-shrink-0 ${
                      currentPage === 'dashboard'
                        ? 'bg-brand-primary text-white shadow-brand'
                        : 'text-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span className="hidden md:inline text-sm">Dashboard</span>
                  </button>
                  <button
                    onClick={() => setCurrentPage('tax-reports')}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg transition-all flex-shrink-0 ${
                      currentPage === 'tax-reports'
                        ? 'bg-brand-primary text-white shadow-brand'
                        : 'text-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span className="hidden md:inline text-sm">Tax</span>
                  </button>
                  <button
                    onClick={() => setCurrentPage('settings')}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-lg transition-all flex-shrink-0 ${
                      currentPage === 'settings'
                        ? 'bg-brand-primary text-white shadow-brand'
                        : 'text-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <SettingsIcon className="w-4 h-4" />
                    <span className="hidden md:inline text-sm">Settings</span>
                  </button>

                  <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1 hidden sm:block"></div>

                  <button
                    onClick={() => setShowNotifications(true)}
                    className="relative p-2 text-gray-600 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0 hidden sm:block"
                  >
                    <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  </button>

                  <button
                    onClick={toggleDarkMode}
                    className="p-2 text-gray-600 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
                  >
                    {darkMode ? <Sun className="w-4 h-4 sm:w-5 sm:h-5" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5" />}
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => {
                        console.log('Globe clicked, current state:', showLanguageMenu);
                        setShowLanguageMenu(!showLanguageMenu);
                      }}
                      className="p-2.5 text-gray-600 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0 transition-all hover:scale-105"
                      title="Select Language"
                    >
                      <Globe className="w-5 h-5" />
                    </button>
{showLanguageMenu && (
                      <div
                        className="fixed inset-0 z-[9999] flex items-start justify-center pt-20"
                        onClick={() => setShowLanguageMenu(false)}
                      >
                        <div
                          className="w-80 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-gray-300 dark:border-gray-600 overflow-hidden"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 border-b-2 border-blue-700">
                            <div className="flex items-center gap-3">
                              <Globe className="w-5 h-5 text-white" />
                              <p className="text-base font-bold text-white uppercase tracking-wide">Select Language</p>
                            </div>
                          </div>
                          <div className="py-2 max-h-96 overflow-y-auto">
                            {languages && languages.length > 0 ? (
                              languages.map((lang) => (
                                <button
                                  key={lang.code}
                                  onClick={() => {
                                    setLanguage(lang.code);
                                    setShowLanguageMenu(false);
                                  }}
                                  className={`w-full text-left px-6 py-4 transition-all flex items-center justify-between ${
                                    language === lang.code
                                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold border-l-4 border-blue-600'
                                      : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 border-l-4 border-transparent'
                                  }`}
                                >
                                  <span className="text-lg">{lang.name}</span>
                                  {language === lang.code && (
                                    <div className="flex items-center justify-center w-7 h-7 bg-blue-600 rounded-full">
                                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-6 py-4 text-gray-500">No languages available</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {(!isDevelopmentMode || isAuthenticated) && (
                    <>
                      <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1 hidden sm:block"></div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-1 px-2 sm:px-3 py-2 text-gray-600 dark:text-gray-100 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-colors flex-shrink-0"
                        title="Logout"
                      >
                        <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-xs sm:text-sm font-medium hidden lg:inline">Logout</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </nav>

          <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
            {isDevelopmentMode && userRole !== null && (
              <div className="mb-6 bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 border-2 border-orange-300 dark:border-orange-700 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                    {userRole === 'super_admin' && <Shield className="w-5 h-5 text-white" />}
                    {userRole === 'team_leader' && <Users className="w-5 h-5 text-white" />}
                    {userRole === 'normal_user' && <User className="w-5 h-5 text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 dark:text-white">
                      Preview Mode: {userRole === 'super_admin' ? 'Super Admin' : userRole === 'team_leader' ? 'Team Leader' : 'Normal User'}
                      {selectedUserId && <span className="ml-2 text-orange-600 dark:text-orange-400">• Viewing as specific user</span>}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {selectedUserId
                        ? "Testing RLS policies - you should only see data this user has access to."
                        : "You're viewing the interface as this user would see it. Select a specific user from the preview panel to test data access."}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {currentPage === 'dashboard' && <Dashboard devModeUserId={isDevelopmentMode ? selectedUserId : actualUserId} />}
            {currentPage === 'tax-reports' && <TaxReports devModeUserId={isDevelopmentMode ? selectedUserId : actualUserId} />}
            {currentPage === 'settings' && <Settings showBotSetup={false} userId={actualUserId || selectedUserId || undefined} />}
          </main>

          <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Managed by <span className="font-semibold text-brand-primary">Lyt Brox Pte Ltd</span>
              </p>
            </div>
          </footer>

          {actualUserId && (
            <NotificationPanel
              userId={actualUserId}
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}
